import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes } from '@angular/router';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
} from '@angular/material/core';
import { FuseModule } from '@fuse/fuse.module';
import { FuseSharedModule } from '@fuse/shared.module';
import { FuseProgressBarModule, FuseSidebarModule } from '@fuse/components';
import { fuseConfig } from 'app/fuse-config';
import { AppComponent } from 'app/app.component';
import { LayoutModule } from 'app/layout/layout.module';
import { ModulesModule } from '@modules/modules.module';
import { APP_BASE_HREF, Location, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeCH from '@angular/common/locales/de-CH';
import localeDeAT from '@angular/common/locales/de-AT';
import localeDeBE from '@angular/common/locales/de-BE';
import localeDeIT from '@angular/common/locales/de-IT';
import localeDeLI from '@angular/common/locales/de-LI';
import localeDeLU from '@angular/common/locales/de-LU';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeFrCH from '@angular/common/locales/fr-CH';
import {
    MAT_SNACK_BAR_DEFAULT_OPTIONS,
    MatSnackBarModule
} from '@angular/material/snack-bar';
import { CoreModule } from '@core/core.module';
import { HttpErrorInterceptor } from '@shared/interceptors/http-error.interceptor';
import { SNACK_BAR_CONFIG__DEFAULT } from '@shared/constants/snack-bar';
import { WorkFlowModule } from '@modules/bcm/@work-flows/work-flow.module';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { DateFnsConfigurationService, DateFnsModule } from 'ngx-date-fns';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';
import { CloseScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { getGermanPaginatorIntl } from '@core/intl/get-german-paginator-intl';
import { SystemNotificationsModule } from '@core/components/system-notifications/system-notifications.module';
import { WelcomeSplashModule } from '@core/components/welcome-splash/welcome-splash.module';
import { SocketIoConfig, SocketIoModule } from 'ngx-socket-io';
import { ToastrModule } from 'ngx-toastr';
import { DragToSelectModule } from 'ngx-drag-to-select';
import { DateFnsAdapter, MAT_DATE_FNS_DATE_FORMATS } from './date-fns-date-adapter.class';
import { ReleaseNotesDialogModule } from '@modules/bcm/@shared/components/dialogs/release-notes/release-notes-dialog.module';
import { getDefaultLocale } from '@core/date.facade';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { MatIconModule } from '@angular/material/icon';
import { SvgIconModule } from '@core/modules/svg-icons/svg-icon.module';
import { AuthenticationModule } from './auth';
import { TokenInterceptor } from '@shared/interceptors/token-interceptor';
import { AuthService } from './auth/auth.service';
import { AuthEventsService } from './auth/auth-events.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { appInitializer } from './app-initializer';
import { BcmTenantService } from '@modules/bcm/bcm-tenant.service';
import { BcmService } from '@modules/bcm/bcm.service';
import { TenantApiService } from '@bcmApiServices/tenant.api-service';
import { environment } from '@env/environment';
import { PrimeNG, providePrimeNG } from 'primeng/config';
import { buildPrimeNgTranslation } from '@core/translation/primeng-translation';
import { DialogService } from 'primeng/dynamicdialog';
import { U2bPreset } from './theme/primeng-preset';

registerLocaleData(localeDe, 'de-DE');
registerLocaleData(localeDeCH, 'de-CH');
registerLocaleData(localeDeAT, 'de-AT');
registerLocaleData(localeDeBE, 'de-BE');
registerLocaleData(localeDeIT, 'de-IT');
registerLocaleData(localeDeLI, 'de-LI');
registerLocaleData(localeDeLU, 'de-LU');
registerLocaleData(localeEn, 'en-US');
registerLocaleData(localeFr, 'fr-FR');
registerLocaleData(localeFrCH, 'fr-CH');

const deConfig = new DateFnsConfigurationService();
deConfig.setLocale(getDefaultLocale());

const appRoutes: Routes = [
    {
        path: '**',
        redirectTo: '',
    }
];

export function scrollFactory(overlay: Overlay): () => CloseScrollStrategy {
    return () => overlay.scrollStrategies.close();
}

const socketIoConfig: SocketIoConfig = {url: 'http://localhost:1337', options: {}};

@NgModule({ declarations: [
        AppComponent,
    ],
    bootstrap: [
        AppComponent
    ], imports: [BrowserModule,
        SocketIoModule.forRoot(socketIoConfig),
        BrowserAnimationsModule,
        RouterModule.forRoot(appRoutes, {
            onSameUrlNavigation: 'reload',
            scrollPositionRestoration: 'enabled',
            paramsInheritanceStrategy: 'always',
            // enableTracing: true // <-- debugging purposes only
        }),
        DateFnsModule.forRoot(),
        DragToSelectModule.forRoot(),
        NgIdleKeepaliveModule.forRoot(),
        HammerModule,
        // Fuse modules
        FuseModule.forRoot(fuseConfig),
        FuseProgressBarModule,
        FuseSharedModule,
        FuseSidebarModule,
        // App modules
        LayoutModule,
        ModulesModule,
        CoreModule,
        MatSnackBarModule,
        WorkFlowModule,
        ReleaseNotesDialogModule,
        SystemNotificationsModule,
        WelcomeSplashModule,
        ToastrModule.forRoot({
            timeOut: 8000,
            positionClass: 'toast-top-right',
            extendedTimeOut: 2000,
            preventDuplicates: true,
        }),
        MatIconModule,
        SvgIconModule,
        AuthenticationModule], providers: [
        { provide: APP_BASE_HREF, useValue: environment.baseHref || '/' },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializer,
            multi: true,
            deps: [AuthService, AuthEventsService, TolgeeService, BcmTenantService, TenantApiService, BcmService, Location]
        },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        // {provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true},
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
        { provide: LOCALE_ID, useValue: 'de-DE' },
        { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
        { provide: DateFnsConfigurationService, useValue: deConfig },
        { provide: DateAdapter, useClass: DateFnsAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_DATE_FORMATS },
        { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: SNACK_BAR_CONFIG__DEFAULT },
        { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { panelClass: 'default-dialog', hasBackdrop: true } },
        { provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false } },
        { provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] },
        { provide: MatPaginatorIntl, useValue: getGermanPaginatorIntl() },
        provideHttpClient(withInterceptorsFromDi()),
        provideZoneChangeDetection(),
        providePrimeNG({
            theme: {
                preset: U2bPreset,
                options: { darkModeSelector: false, cssLayer: false },
            },
        }),
        DialogService,
    ] })
export class AppModule {
    constructor(library: FaIconLibrary, primeng: PrimeNG, tolgee: TolgeeService) {
        library.addIconPacks(fas, fab, far);
        // Localize PrimeNG datepicker button-bar (Today/Clear) + calendar names;
        // re-applied whenever the active language changes (and after Tolgee loads).
        tolgee.language$.subscribe(lang => primeng.setTranslation(buildPrimeNgTranslation(tolgee, lang)));
    }
}
